import { Canceler } from 'axios';
import { observer } from 'mobx-react';
import { Component } from 'react';
import PreviousOrdersListOrder from '../../components/PreviousOrdersListOrder/PreviousOrdersListOrder';
import { withCheckoutStore } from '../../context';
import { defaultPagination } from 'src/constants/main';
import { ORDER_STATUS } from 'src/constants/order';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { IPagination } from 'src/types/main';
import { TOrder } from 'src/types/orders';
import { arrayMerge, formatPrice } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import ScrollableList from 'src/view/components/ScrollableList/ScrollableList';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { CheckoutStore } from '~/mobx/checkoutStore';
import logger from '~/services/logger';
import './style.scss';

interface IState {
  pagination: IPagination<TOrder>;
  isLoading: boolean;
  pages: number[];
}

// @ts-ignore
@withCheckoutStore
@observer
export default class PreviousOrdersList extends Component<{ checkoutStore?: CheckoutStore }, IState> {
  cancel: Canceler | null = null;

  state: IState = {
    pagination: defaultPagination,
    isLoading: false,
    pages: [],
  };

  get checkoutStore() {
    return this.props.checkoutStore!;
  }

  componentDidMount = () => {
    this.fetchOrders();
  };

  componentWillUnmount = () => {
    if (this.cancel) this.cancel('Cancelled');
  };

  fetchOrders = async (page: number = this.state.pagination.current_page + 1) => {
    try {
      if (this.cancel) this.cancel('Cancelled');

      this.setState({ isLoading: true });

      const pagination = await client.get(`/v3/orders`, {
        params: { page, is_delete: 0, is_donation: 0, user_id: this.checkoutStore.dependencies.customer?.id, status: [ORDER_STATUS.COMPLETED] },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      let data: TOrder[] = [];
      let pages: number[] = this.state.pages;

      if (
        (pagination.current_page === 1 && !this.state.pages.length) ||
        (pagination.current_page <= 1 && !pagination.data.length) ||
        !this.state.pagination.current_page
      ) {
        data = pagination.data;
        pages = [];
      } else if (this.state.pagination.current_page > page) {
        data = arrayMerge(pagination.data, this.state.pagination.data, 'id');
      } else {
        data = arrayMerge(this.state.pagination.data, pagination.data, 'id');
      }

      pages = [...this.state.pages, pagination.current_page].sort((a, b) => (a > b ? 1 : -1));
      this.setState({ pagination: { ...pagination, data }, isLoading: false, pages });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.setState({ isLoading: false });
      logger.error({ error, place: 'PreviousOrdersList fetchOrders' });
    }
  };

  renderOrder = (order: TOrder | undefined) => {
    return <PreviousOrdersListOrder order={order} />;
  };

  get averageCustomerSpending(): string | null {
    if (!this.state.pagination.avg_spending) return null;
    return `${localeStore.t('average_spending')}: ${formatPrice(this.state.pagination.avg_spending)}`;
  }

  render() {
    return (
      <div className="previous-orders-list">
        {/* header */}
        <Text type={TEXT.SECTION_HEADER} text="previous_orders" className="mt-2 mb-4" />
        {/* previous orders list */}
        <ScrollableList
          autoFetch
          scrollbar
          scrollToTop={false}
          direction="horizontal"
          fetch={this.fetchOrders}
          list={this.state.pagination.data}
          loading={this.state.isLoading}
          refreshing={this.state.isLoading}
          current_page={this.state.pagination.current_page}
          last_page={this.state.pagination.last_page}
          renderItem={this.renderOrder}
          pages={this.state.pages}
          className="previous-orders-list__scrollable-list"
          listClassName="gap-20"
        />
        {/* average spending */}
        {this.state.pagination.avg_spending ? (
          <Text type={TEXT.ORDER_FIELD} text={this.averageCustomerSpending} noTranslate className="previous-orders-list__average-spending mt-6" />
        ) : null}
      </div>
    );
  }
}
